import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaPlus } from 'react-icons/fa';
import { FaX } from 'react-icons/fa6';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button, Checkbox,
  HStack,
  Icon,
  IconButton,
  Text,
  Tooltip
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { CypressIds } from '../../../../../../../../../../constants/cypressIds';
import { CertSubmissionDuskIds } from '../../../../../../../../../../constants/duskIds';
import { createErrorToast, createSuccessToast, createToast } from '../../../../../../../../../../redux/modules/app';
import { hideModal } from '../../../../../../../../../../redux/modules/modal';
import {
  useCompanyQuestionCategories,
  useCreateQuestionCategory
} from '../../../../../../../../../../services/company-service';
import { getInfiniteListData } from '../../../../../../../../../../services/util';
import FormInput from '../../../../../../../../../form/FormInput';
import InputLabel from '../../../../../../../../../form/InputLabel';
import { InfiniteDropdownMenu } from '../../../../../../../../../library';

const EditQuestionModal = ({ question, editQuestion }) => {
  const dispatch = useDispatch();
  const { companyId } = question;
  const [questionCategoryId, setQuestionCategoryId] = useState(question?.questionCategoryId);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [confirmCheckbox, setConfirmCheckbox] = useState(false);

  const {
    data: infQuestionCategories,
    isLoading,
    hasNextPage,
    fetchNextPage
  } = useCompanyQuestionCategories({ companyId, withDeleted: false });
  const { mutateAsync: createQuestionCategory } = useCreateQuestionCategory({ companyId });

  const useFormMethods = useForm({
    defaultValues: {
      question_name: question?.name || '',
      question_description: question?.question || '',
      question_red_flag: question?.redFlag || false,
      retroactive: false
    }
  });
  const { handleSubmit } = useFormMethods;

  const questionCategories = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return getInfiniteListData(infQuestionCategories);
  }, [infQuestionCategories, isLoading]);

  const onSubmit = async data => {
    if ((showCreateCategory && !data?.newCategoryName) || (!showCreateCategory && !questionCategoryId)) {
      dispatch(createErrorToast({ title: 'Please provide a new question category name or select an existing category' }));

      return;
    }

    let questionCategoryIdToUse = questionCategoryId;

    if (showCreateCategory && data?.newCategoryName) {
      const response = await createQuestionCategory({ name: data.newCategoryName });

      if (!response?.id) {
        dispatch(createErrorToast({ title: 'There was an issue creating the question category.  Please try again.' }));

        return;
      }

      questionCategoryIdToUse = response.id;
    }

    if (questionCategoryIdToUse === question.questionCategoryId
      && question.name === data.question_name
      && question.question === data.question_description
      && !!question.redFlag === !!data.question_red_flag
    ) {
      dispatch(createSuccessToast({ title: 'Nothing changed!' }));

      dispatch(hideModal());

      return;
    }

    let updateMessage = 'Updating question...';

    if (questionCategoryIdToUse !== question.questionCategoryId
      && question.name === data.question_name
      && question.question === data.question_description
      && !!question.redFlag === !!data.question_red_flag
    ) {
      updateMessage = 'Updating question category id and question...';
    }

    dispatch(createToast({ title: updateMessage }));

    await editQuestion({
      questionId: question.id,
      questionCategoryId: questionCategoryIdToUse,
      name: data.question_name,
      question: data.question_description,
      redFlag: data.question_red_flag,
      retroactive: true // always retroactive update
    });

    dispatch(hideModal());
  };

  const initialQuestionCategory = useMemo(() => {
    const qCId = question?.questionCategoryId;

    if (questionCategories && questionCategories?.length) {
      const exist = questionCategories.find(qC => qC.id === qCId);

      if (exist) {
        return {
          id: qCId,
          label: exist.name,
          ...exist
        };
      }
    }

    return null;
  }, [questionCategories]);

  return (
    <Box>
      <Text mb={6} textStyle="bodyXxl500">Edit Question</Text>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box mb="26px">
          <InputLabel
            required
            labelName="Question Category"
          />
          <HStack>
            <InfiniteDropdownMenu
              data={questionCategories}
              fetchMoreData={fetchNextPage}
              idKey="question-category-dropdown"
              initialSelect={initialQuestionCategory}
              isDisabled={showCreateCategory}
              labelKey="name"
              pageInfo={hasNextPage ? { currentPage: 1, lastPage: 2 } : { currentPage: 1, lastPage: 1 }}
              scrollClassName="question-category-dropdown"
              variant="primary"
              onSelect={category => setQuestionCategoryId(category.id)}
            />
            <Box>
              <Tooltip
                hasArrow
                borderRadius="md"
                label={`${showCreateCategory ? 'Cancel' : 'Create new category'}`}
                placement="bottom"
              >
                <IconButton
                  aria-label="create-new-question-category-button"
                  data-cy={CypressIds.editQuestionModalCreateNewCategoryButton}
                  icon={<Icon as={showCreateCategory ? FaX : FaPlus} width={9} />}
                  variant="gradientAi"
                  onClick={() => setShowCreateCategory(!showCreateCategory)}
                />
              </Tooltip>
            </Box>
          </HStack>
        </Box>

        {showCreateCategory && (
        <Box my={4}>
          <FormInput
            cypressId={CypressIds.editQuestionModalCreateNewCategoryInput}
            inputName="newCategoryName"
            isDisabled={!showCreateCategory}
            labelName="Question Category Name"
            placeholder="Type Your New Category Name Here"
            useFormMethods={useFormMethods}
            validationRules={{ required: { value: true, message: 'New Question Category Name is required' } }}
          />
        </Box>
        )}

        <FormInput
          required
          cypressId={CypressIds.editQuestionModalNameInput}
          inputName="question_name"
          labelName="Name"
          useFormMethods={useFormMethods}
        />

        <FormInput
          required
          cypressId={CypressIds.editQuestionModalDescriptionInput}
          inputName="question_description"
          labelName="Question"
          useFormMethods={useFormMethods}
        />

        <FormInput
          cypressId={CypressIds.editQuestionModalRedFlagSwitch}
          inputName="question_red_flag"
          labelName="Red Flag Question"
          toolTip="When enabled, if the answer to this question is 'yes', the answer will show a red flag instead of a green check mark.
              Likewise, if the answer is 'no', the answer will show a green check mark.
              Use this when you want PINS AI Assistant to find something that would cause the requirement to be non-compliant."
          type="switch"
          useFormMethods={useFormMethods}
        />

        <HStack alignItems="flex-start" justifyContent="center" mb={4} width="100%">
          <Checkbox
            borderColor="greyScale.700"
            data-cy={CypressIds.editQuestionModalRetroactiveCheckbox}
            id="edit-question-acknowledge-checkbox"
            isChecked={confirmCheckbox}
            size="lg"
            onChange={() => setConfirmCheckbox(prev => !prev)}
          />
          <Text
            cursor="pointer"
            onClick={() => {
              const acknowledgeCheckBox = document.getElementById('edit-question-acknowledge-checkbox');

              acknowledgeCheckBox.dispatchEvent(new MouseEvent('click', { bubbles: true }));
            }}
          >
            I understand that PINS will update all records and templates that use this question in their requirements.
            This will remove any existing answers to the previous question, as they may no longer be accurate.
            If you would like to avoid this, PINS recommends creating a new question.
          </Text>
        </HStack>

        <HStack justifyContent="center">
          <Button data-cy={CypressIds.editQuestionModalCancelButton} mr={3} variant="ghost" onClick={() => dispatch(hideModal())}>
            Cancel
          </Button>
          <Button
            data-cy={CypressIds.editQuestionModalSaveButton}
            h="48px"
            isDisabled={!confirmCheckbox || !questionCategoryId}
            type="submit"
            variant="gradientAi"
          >
            Save Changes
          </Button>
        </HStack>
      </form>
    </Box>
  );
};

EditQuestionModal.propTypes = {
  question: PropTypes.object.isRequired,
  editQuestion: PropTypes.func.isRequired
};

export default EditQuestionModal;
